import Link from 'next/link';
import React, {
  CSSProperties,
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { SchemaObject, ParameterObject } from 'openapi3-ts';
import Touchable from 'plaid-threads/Touchable';
import Button from 'plaid-threads/Button';
import snarkdown from 'snarkdown';
import cx from 'classnames';

import Context from '../../../../contexts/docs';
import ChevronS2Down from 'plaid-threads/Icons/ChevronS2Down';
import ChevronS2Up from 'plaid-threads/Icons/ChevronS2Up';
import styles from './SchemaRow.module.scss';
import AdditionalProperties from './AdditionalProperties';
import {
  addWordBreaks,
  createId,
  snarkdownWithParagraphs,
} from '../../utilities';
import {
  getIndentLevel,
  isExpandable,
  normalizeSchemaForRendering,
} from './schemaUtils';
import AttributeTypesLabel from './AttributeTypesLabel';
import OneOfOptions, { getOneOfAlternativeFieldNames } from './OneOfOptions';

type SchemaObjectEntry = [
  string,
  (
    | SchemaObject['properties']
    | { 'x-hidden-from-docs'?: boolean }
    | { 'x-hidden-behind-flag'?: string }
  ),
];

interface Props {
  schemaName: string;
  schemaEntry: SchemaObjectEntry;
  pathEntry?: ParameterObject;
  required?: boolean;
  deprecated?: boolean;
  depth: number;
  maxDepth?: number;
  parentHeight?: number;
  expandAll: boolean;
  expandText?: string;
  hideText?: string;
  type?: string;
  topMargin?: number;
  parentKeys: [] | string[];
  unhideFlag?: string;
  responseExamples?: SchemaObject['examples'];
  stringCode?: string;
  route?: string;
  exchangeSpec?: string;
  coreExchangeSpec?: string;
  onLineNumbers?: (lineStart: number | null) => void;
}

const shouldRenderSchemaItemArrayMembers = (
  schemaItem: SchemaObject,
): boolean => {
  return schemaItem.type === 'array' && schemaItem.items != null;
};

type ExamplePathSegment = string | number;

// Walk `path` (a list of object keys) down through a JSON response example,
// returning the concrete path actually taken — object keys plus the array
// index chosen at each array level — or `null` if the path doesn't exist.
// Arrays are treated as "any element": each element is tried until one
// resolves the remaining path, so a field that only appears in a non-first
// array element (e.g. `payroll_income_results` in `sessions[1].results`) is
// found rather than being missed by always descending into element 0.
// Recording the index (rather than just the value) lets the caller locate the
// field's exact position in the serialized example, even when sibling elements
// serialize identically.
const resolveConcretePath = (
  value: unknown,
  path: string[],
): ExamplePathSegment[] | null => {
  // A fully-consumed path resolves even when `value` is `null`, so a
  // present-but-null field still counts as existing.
  if (path.length === 0) return [];
  if (value == null) return null;
  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const rest = resolveConcretePath(value[i], path);
      if (rest != null) return [i, ...rest];
    }
    return null;
  }
  if (typeof value !== 'object') return null;
  const [head, ...tail] = path;
  if (!(head in (value as Record<string, unknown>))) return null;
  const rest = resolveConcretePath(
    (value as Record<string, unknown>)[head],
    tail,
  );
  return rest == null ? null : [head, ...rest];
};

// Number of lines `value` spans when serialized with `JSON.stringify(_, 2)`,
// matching the formatting of the displayed example (see getFormattedExample).
const countRenderedLines = (value: unknown): number =>
  JSON.stringify(value, null, 2).split('\n').length;

// 1-indexed line on which the value at `concretePath` begins within
// `JSON.stringify(root, null, 2)`. The container opens on line 1; each nested
// member starts one line below its container's bracket plus the rendered
// height of every preceding sibling. This mirrors the pretty-printer exactly,
// so it stays in lockstep with the rendered example without string scanning.
const lineOfConcretePath = (
  root: unknown,
  concretePath: ExamplePathSegment[],
): number => {
  let line = 1;
  let node: unknown = root;
  for (const segment of concretePath) {
    if (Array.isArray(node)) {
      let elementLine = line + 1;
      for (let i = 0; i < (segment as number); i++) {
        elementLine += countRenderedLines(node[i]);
      }
      line = elementLine;
      node = node[segment as number];
    } else {
      let propertyLine = line + 1;
      for (const propertyKey of Object.keys(node as Record<string, unknown>)) {
        if (propertyKey === segment) break;
        propertyLine += countRenderedLines(
          (node as Record<string, unknown>)[propertyKey],
        );
      }
      line = propertyLine;
      node = (node as Record<string, unknown>)[segment as string];
    }
  }
  return line;
};

const SchemaRow: React.FC<Props> = (props) => {
  const [isOpen, setIsOpen] = useState(props.expandAll);
  const [itemExistsInExample, setItemExistsInExample] = useState(true);
  const [lineStart, setLineStart] = useState<number | null>(null);
  const [lineEnd, setLineEnd] = useState<number | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isHighlightedFromCodeClick, setIsHighlightedFromCodeClick] =
    useState(false);
  const { dispatch, selectedCode } = useContext(Context);
  const [myStickyHeight, setMyStickyHeight] = useState(0);
  const [isStuck, setIsStuck] = useState(false);

  // Memoize schema composition normalization. It walks the schema tree and is
  // non-trivial; on a Schema-heavy page (e.g. /docs/api/products/*), running
  // it on every render of every SchemaRow is a substantial cost.
  //
  // Dep is keyed on schemaEntry[1] (the schema object reference) rather
  // than schemaEntry (the tuple) because the recursive parent passes a
  // fresh Object.entries(...) tuple each render — Object.is on tuples
  // would never match and defeat the memo. The schema OBJECTS inside
  // those tuples are stable references across renders (they come from
  // the parsed OpenAPI spec).
  // https://swagger.io/docs/specification/data-models/inheritance-and-polymorphism/
  const mergedSchemaItem = useMemo<SchemaObject>(() => {
    const item: SchemaObject = props.schemaEntry[1];
    return normalizeSchemaForRendering(item);
  }, [props.schemaEntry[1]]);

  // Copied, not assigned into: resolving references leaves one schema object
  // shared by every place that referenced it, so writing this parameter's
  // description onto it would surface wherever else that schema renders.
  // Memoized to keep a stable identity for the effects below.
  const pathEntrySchemaItem = useMemo<SchemaObject | undefined>(
    () =>
      props.pathEntry == null
        ? undefined
        : {
            ...props.pathEntry.schema,
            description: props.pathEntry.description,
          },
    [props.pathEntry?.schema, props.pathEntry?.description],
  );

  let key: string;
  let id;
  let schemaItem: SchemaObject;
  let schemaItemSubItems: SchemaObject;
  let pathEntryIn: string = '';
  if (props.pathEntry) {
    key = props.pathEntry.name;
    id = createId(props.schemaName, key, props.parentKeys);
    schemaItem = pathEntrySchemaItem;
    pathEntryIn = props.pathEntry.in;
  } else {
    key = props.schemaEntry[0];
    id = createId(props.schemaName, key, props.parentKeys);
    schemaItem = mergedSchemaItem;
    schemaItemSubItems = schemaItem.items;
  }
  const itemPlusParents = `${props.route}/${props.parentKeys.join('/')}/${key}`;

  // snarkdown parses the description string into HTML. Without memoization
  // this runs on every render of every SchemaRow.
  const descriptionHtml = useMemo(
    () =>
      schemaItem.description
        ? snarkdownWithParagraphs(snarkdown, schemaItem.description)
        : '',
    [schemaItem.description],
  );

  React.useEffect(() => setIsOpen(props.expandAll), [props.expandAll]);

  const showLine = () => {
    if (selectedCode.item === itemPlusParents) {
      dispatch({
        type: 'SET_SELECTED_CODE',
        payload: {
          route: null,
          item: null,
          startLine: null,
          endLine: null,
        },
      });

      return;
    }
    dispatch({
      type: 'SET_SELECTED_CODE',
      payload: {
        route: props.route,
        item: itemPlusParents,
        startLine: lineStart,
        endLine: lineEnd,
      },
    });
  };
  // Stable ref to parentKeys. The array reference is new each render
  // (the recursive child JSX does `[...props.parentKeys, key]`), but the
  // contents are fixed per SchemaRow instance — a row's position in the
  // tree doesn't change. Reading via ref keeps props.parentKeys out of
  // the useCallback dep array below, so the callback identity is stable
  // and the `[setExampleLineNumbers]` effect runs once per row instead
  // of every render.
  const parentKeysRef = useRef(props.parentKeys);
  parentKeysRef.current = props.parentKeys;

  // set state of json
  const setExampleLineNumbers = useCallback(async () => {
    try {
      const exampleRoot = Object.values(props.responseExamples)[0].value; // complete json response examples object
      const parentKeys = parentKeysRef.current.map((parentKey) => {
        return parentKey.split('-').join('_');
      });
      // Concrete path (keys + chosen array indices) to this row's field, or
      // null when the field isn't present in the example.
      const concretePath = resolveConcretePath(exampleRoot, [
        ...parentKeys,
        key,
      ]);
      if (concretePath == null) {
        setItemExistsInExample(false);
        setLineEnd(null);
        setLineStart(null);
        return;
      }
      if (
        props.responseExamples != null &&
        (props.type === 'response' || props.type === 'model')
      ) {
        setItemExistsInExample(true);
      }

      if (props.stringCode != null) {
        // Line numbers are derived structurally from the same object the
        // displayed example is serialized from, so they stay correct even
        // when sibling array elements serialize identically.
        const finalValue = concretePath.reduce<unknown>(
          (node, segment) => (node as Record<string, unknown>)[segment],
          exampleRoot,
        );
        const lineStart = lineOfConcretePath(exampleRoot, concretePath);
        setLineStart(lineStart);
        setLineEnd(lineStart + countRenderedLines(finalValue) - 1);
      }
    } catch (err) {
      setItemExistsInExample(false);
      setLineStart(null);
      setLineEnd(null);
    }
  }, [props.responseExamples, key, props.type, props.stringCode]);

  const stickyHeaderRef = useRef(null);
  const H4_HEADER_HEIGHT = 36; // Account for sticky H4 headers on reference pages

  // This is a check to determine if our header is "stuck" based on its y
  // position. We do this so we can add the little bit of shadow
  // underneath the header, but there might be other fun things we could do
  // in the future.
  const checkStickinessBasedOnPosition = () => {
    const HEADER_MIN_HEIGHT = 36;
    const topMarginVal = (props.topMargin ?? 96) + H4_HEADER_HEIGHT;
    const targetOffset = topMarginVal - HEADER_MIN_HEIGHT;
    const y = stickyHeaderRef.current.getBoundingClientRect().top;
    if (y <= myStickyHeight + targetOffset && y > 0 && !isStuck) {
      setIsStuck(true);
    } else if (isStuck && (y < 0 || y > myStickyHeight + topMarginVal)) {
      setIsStuck(false);
    }
  };

  useEffect(() => {
    if (
      isExpandable(schemaItem) &&
      stickyHeaderRef != null &&
      'IntersectionObserver' in window
    ) {
      checkStickinessBasedOnPosition();
      const container = document.querySelector('.docs-scroll-container');
      // With our IntersectionObserver, we can have items listen to onScroll
      // events only when they're on screen.
      const observer = new IntersectionObserver(
        ([e]) => {
          if (e.isIntersecting) {
            container.addEventListener(
              'scroll',
              checkStickinessBasedOnPosition,
            );
          } else {
            container.removeEventListener(
              'scroll',
              checkStickinessBasedOnPosition,
            );
          }
        },
        { threshold: [1] },
      );
      observer.observe(stickyHeaderRef.current);
      return () => {
        container.removeEventListener('scroll', checkStickinessBasedOnPosition);
        observer.disconnect();
      };
    }
  }, [schemaItem, stickyHeaderRef, myStickyHeight, isStuck]);

  useEffect(() => {
    setExampleLineNumbers();
  }, [setExampleLineNumbers]);

  // Report line numbers to parent for valid line detection
  useEffect(() => {
    if (props.onLineNumbers && lineStart !== null) {
      props.onLineNumbers(lineStart);
    }
  }, [lineStart, props.onLineNumbers]);

  // Check if this SchemaRow should be highlighted from code click
  useEffect(() => {
    // Only respond to code clicks, not SchemaRow clicks
    if (
      selectedCode.route === props.route &&
      selectedCode.item === '__CODE_CLICK__' &&
      selectedCode.startLine !== null &&
      lineStart !== null &&
      lineEnd !== null
    ) {
      // Convert from 0-based to 1-based for comparison
      const clickedLine = selectedCode.startLine + 1;
      const isInRange = clickedLine === lineStart;

      setIsHighlightedFromCodeClick(isInRange);
    } else if (selectedCode.item !== '__CODE_CLICK__') {
      // Clear highlights when it's a SchemaRow click (not a code click)
      setIsHighlightedFromCodeClick(false);
    }
  }, [selectedCode, lineStart, lineEnd, props.route, key]);

  // Auto-scroll to highlighted SchemaRow
  useEffect(() => {
    if (isHighlightedFromCodeClick && stickyHeaderRef.current) {
      stickyHeaderRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [isHighlightedFromCodeClick, key]);

  // We have to determine exactly what our y position should be based on the
  // height of any parent headers that might also be sticky -- this is passed
  // down through the parentHeight prop.
  useEffect(() => {
    setMyStickyHeight(
      stickyHeaderRef.current.clientHeight + (props.parentHeight ?? 0),
    );
  }, [schemaItem, props.parentHeight]);

  // When a sticky header is stuck, the browser's native scroll-to-anchor
  // uses the element's rendered (stuck) position rather than its natural
  // document position, requiring multiple clicks to converge. Intercept the
  // click and scroll based on the non-sticky contentRow parent, whose
  // position always reflects the true document layout (sticky doesn't
  // change document flow).
  const handleStickyClick = useCallback(
    (e: React.MouseEvent) => {
      if (!stickyHeaderRef.current) return;

      const header = stickyHeaderRef.current;
      const scrollContainer = document.querySelector(
        '.docs-scroll-container',
      ) as HTMLElement;
      if (!scrollContainer) return;

      // Check if the header is currently stuck by comparing its viewport
      // position to where it would be when stuck (container top + CSS top).
      // This is more reliable than the isStuck state for nested headers.
      const headerRect = header.getBoundingClientRect();
      const cssTop = parseFloat(window.getComputedStyle(header).top || '0');
      const containerRect = scrollContainer.getBoundingClientRect();
      const stuckPosition = containerRect.top + cssTop;
      const headerIsStuck = Math.abs(headerRect.top - stuckPosition) < 5;

      if (!headerIsStuck) return;

      e.preventDefault();

      // The contentRow is not sticky, so its getBoundingClientRect
      // reflects the true document position regardless of which
      // ancestor headers are currently stuck.
      const contentRow = header.parentElement;
      if (!contentRow) return;

      const rowRect = contentRow.getBoundingClientRect();
      const rowDocOffset =
        rowRect.top - containerRect.top + scrollContainer.scrollTop;
      const topPosition = (props.parentHeight ?? 0) + H4_HEADER_HEIGHT;

      scrollContainer.scrollTo({
        top: rowDocOffset - topPosition,
        behavior: 'smooth',
      });
    },
    [props.parentHeight],
  );

  const myIndentLevel = getIndentLevel(props.depth, props.maxDepth);
  const expandedSchema = shouldRenderSchemaItemArrayMembers(schemaItem)
    ? schemaItemSubItems
    : schemaItem;
  const nestedEntries = Object.entries(expandedSchema?.properties || {}).filter(
    (entry: SchemaObjectEntry) => {
      return !(
        (entry[1].hasOwnProperty('x-hidden-from-docs') &&
          entry[1]['x-hidden-from-docs'] === true) ||
        (entry[1].hasOwnProperty('x-hidden-behind-flag') &&
          entry[1]['x-hidden-behind-flag'] !== props.unhideFlag)
      );
    },
  );
  const alternativeFieldNames = new Set(
    getOneOfAlternativeFieldNames(expandedSchema),
  );
  const sharedNestedEntries = nestedEntries.filter(
    ([fieldName]) => !alternativeFieldNames.has(fieldName),
  );
  const alternativeNestedEntries = nestedEntries.filter(([fieldName]) =>
    alternativeFieldNames.has(fieldName),
  );
  const alternativeGuidance = <OneOfOptions schema={expandedSchema} />;
  const renderNestedSchemaRow = (entry: SchemaObjectEntry) => (
    <SchemaRow
      coreExchangeSpec={props.coreExchangeSpec}
      type={props.type}
      schemaName={props.schemaName}
      expandAll={props.expandAll}
      schemaEntry={entry}
      responseExamples={props.responseExamples}
      required={
        schemaItem != null &&
        (schemaItemSubItems?.required != null || schemaItem.required != null) &&
        (props.type === 'request' || props.coreExchangeSpec) &&
        (schemaItemSubItems?.required?.includes(entry[0]) ||
          schemaItem.required?.includes(entry[0]))
      }
      stringCode={props.stringCode}
      route={props.route}
      key={entry[0]}
      maxDepth={props.maxDepth}
      parentKeys={[
        ...props.parentKeys,
        props.schemaEntry[0].split('_').join('-'),
      ]}
      depth={props.depth + 1}
      parentHeight={myStickyHeight}
      topMargin={props.topMargin}
      onLineNumbers={props.onLineNumbers}
    />
  );

  return (
    <div
      className={cx(
        styles.schemaRow,
        props.depth >= 2 && styles.noSeparator,
        isHighlightedFromCodeClick && styles.highlightedFromCode,
      )}
    >
      <div
        className={cx(styles.contentRow)}
        data-algolia='schemaRowTd'
        style={{ '--indent-amount': `${myIndentLevel}rem` } as CSSProperties}
      >
        <header
          className={cx(
            styles.header,
            styles.stickyBit,
            isStuck && styles.stuck,
          )}
          ref={stickyHeaderRef}
          style={
            {
              '--top-position': `${
                (props.parentHeight ?? 0) + H4_HEADER_HEIGHT
              }px`, // Add 36px for H4 header
              '--sticky-z-index': 20 - props.depth * 2,
            } as CSSProperties
          }
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Link
            className='hide-for-aeo'
            href={`#${id}`}
            onClick={handleStickyClick}
          >
            <code
              className={styles.attributeKey}
              data-algolia='schemaRowAttributeKey'
            >
              <>{addWordBreaks(key)}</>
            </code>
            <span
              id={id}
              className={cx(styles.offsetAnchor, styles.scrollyBit)}
              style={
                {
                  '--scroll-margin-amount': `${
                    (props.parentHeight ?? 0) + H4_HEADER_HEIGHT
                  }px`,
                } as CSSProperties
              }
              data-algolia='schemaRowAnchor'
            />
          </Link>
          <div className={cx(styles.attributesContainer)}>
            {itemExistsInExample &&
              isHovered &&
              (selectedCode?.item === itemPlusParents ? (
                <Button
                  secondary
                  inline
                  onClick={showLine}
                  className={styles.viewCode}
                  centered
                  small
                  aria-label='view JSON'
                >
                  View all
                </Button>
              ) : isHovered ? (
                <Button
                  secondary
                  inline
                  onClick={showLine}
                  className={styles.viewCode}
                  centered
                  small
                  aria-label='view JSON'
                >
                  JSON
                </Button>
              ) : (
                <></>
              ))}
            <AttributeTypesLabel
              schemaItem={schemaItem}
              schemaItemSubItems={schemaItemSubItems}
              required={props.required}
              type={props.type}
              exchangeSpec={props.exchangeSpec}
              coreExchangeSpec={props.coreExchangeSpec}
            />
          </div>
        </header>
        <div className={cx(styles.attributes)}>
          {schemaItem.description && (
            <div
              className={styles.description}
              data-algolia='schemaRowDescription'
            >
              <div dangerouslySetInnerHTML={{ __html: descriptionHtml }} />

              <AdditionalProperties schemaItem={schemaItem} />
              {props.pathEntry && (
                <div>
                  Parameter type:
                  <code> {pathEntryIn}</code>
                </div>
              )}
            </div>
          )}

          {isExpandable(schemaItem) && key !== 'core_attributes' && (
            <Touchable
              onClick={() => setIsOpen(!isOpen)}
              className={cx(styles.attributesLink, 'hide-for-aeo')}
            >
              {isOpen
                ? props.hideText || 'Hide object'
                : props.expandText
                ? `${props.expandText}…`
                : 'View object…'}

              {isOpen ? <ChevronS2Up /> : <ChevronS2Down />}
            </Touchable>
          )}
        </div>
        {isOpen && isExpandable(schemaItem) && key !== 'core_attributes' && (
          <div className={styles.nestedTable}>
            <div>
              {alternativeNestedEntries.length === 0 && alternativeGuidance}
              {sharedNestedEntries.map(renderNestedSchemaRow)}
              {alternativeNestedEntries.length > 0 && (
                <>
                  {alternativeGuidance}
                  {alternativeNestedEntries.map(renderNestedSchemaRow)}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

SchemaRow.displayName = 'SchemaRow';

export default SchemaRow;
